import type { MouseEventHandler, ReactNode } from 'react';
import { useCallback, useRef } from 'react';
import { Badge, Box, Chip, Line, Spinner, Text, Tooltip, as, color, toRem } from 'folds';
import { TooltipProvider } from '$components/overlay-stack';
import type { MatrixError, Room, IHierarchyRoom } from '$types/matrix-sdk';
import { JoinRule, KnownMembership } from '$types/matrix-sdk';
import { RoomAvatar, RoomIcon } from '$components/room-avatar';
import { Avatar, roomAvatarShape } from '$components/avatar';
import { SequenceCard } from '$components/sequence-card';
import { useMatrixClient } from '$hooks/useMatrixClient';
import type { HierarchyItem } from '$hooks/useSpaceHierarchy';
import { KnockRoomPrompt } from '$components/knock-room-prompt';
import { LocalRoomSummaryLoader } from '$components/RoomSummaryLoader';
import { UseStateProvider } from '$components/UseStateProvider';
import { RoomTopicViewer } from '$components/room-topic-viewer';
import { onEnterOrSpace } from '$utils/keyboard';

import { AsyncStatus, useAsyncCallback } from '$hooks/useAsyncCallback';
import { getDirectRoomAvatarUrl, getRoomAvatarUrl } from '$utils/room/display';
import { nameInitials } from '$utils/common';
import { mxcUrlToHttp } from '$utils/matrix';
import { useMediaAuthentication } from '$hooks/useMediaAuthentication';
import { formatCompactNumber } from '$utils/formatCompactNumber';
import { ItemDraggableTarget, useDraggableItem } from './DnD';
import * as dndCss from './DnD.css';
import {
  ArrowRight,
  chipIcon,
  composerIcon,
  EnvelopeSimple,
  Plus,
  Warning,
} from '$components/icons/phosphor';
import * as styleCss from './style.css';
import * as css from './RoomItem.css';
import { ModalOverlay } from '$components/modal-overlay/ModalOverlay';

type RoomJoinButtonProps = {
  roomId: string;
  via?: string[];
};
function RoomJoinButton({ roomId, via }: RoomJoinButtonProps) {
  const mx = useMatrixClient();
  const [joinState, join] = useAsyncCallback<Room, MatrixError, []>(
    useCallback(() => mx.joinRoom(roomId, { viaServers: via }), [mx, roomId, via])
  );

  const canJoin = joinState.status === AsyncStatus.Idle || joinState.status === AsyncStatus.Error;

  return (
    <Box shrink="No" gap="200" alignItems="Center">
      {joinState.status === AsyncStatus.Error && (
        <TooltipProvider
          tooltip={
            <Tooltip variant="Critical" style={{ maxWidth: toRem(200) }}>
              <Box direction="Column" gap="100">
                <Text style={{ wordBreak: 'break-word' }} size="T400">
                  {joinState.error.data?.error || joinState.error.message}
                </Text>
                <Text size="T200">{joinState.error.name}</Text>
              </Box>
            </Tooltip>
          }
        >
          {(triggerRef) => (
            <Box
              ref={triggerRef}
              style={{ color: color.Critical.Main, cursor: 'pointer', display: 'flex' }}
              tabIndex={0}
              aria-label={joinState.error.data?.error || joinState.error.message}
            >
              {composerIcon(Warning, { weight: 'fill' })}
            </Box>
          )}
        </TooltipProvider>
      )}
      <Chip
        variant="Secondary"
        fill="Soft"
        size="400"
        radii="Pill"
        before={canJoin ? chipIcon(Plus) : <Spinner variant="Secondary" size="100" />}
        onClick={join}
        disabled={!canJoin}
      >
        <Text size="B300">Join</Text>
      </Chip>
    </Box>
  );
}

function RoomKnockButton({ roomId, via }: RoomJoinButtonProps) {
  return (
    <UseStateProvider initial={false}>
      {(knocking, setKnocking) => (
        <Box shrink="No" gap="200" alignItems="Center">
          <Chip
            variant="Secondary"
            fill="Soft"
            size="400"
            radii="Pill"
            before={chipIcon(EnvelopeSimple)}
            onClick={() => setKnocking(true)}
          >
            <Text size="B300">Knock</Text>
          </Chip>
          {knocking && (
            <KnockRoomPrompt
              roomId={roomId}
              via={via}
              onDone={() => setKnocking(false)}
              onCancel={() => setKnocking(false)}
            />
          )}
        </Box>
      )}
    </UseStateProvider>
  );
}

type RoomJoinOrKnockButtonProps = {
  roomId: string;
  via?: string[];
  joinRule?: JoinRule;
};
function RoomJoinOrKnockButton({ roomId, via, joinRule }: RoomJoinOrKnockButtonProps) {
  if (joinRule === JoinRule.Knock) {
    return <RoomKnockButton roomId={roomId} via={via} />;
  }
  return <RoomJoinButton roomId={roomId} via={via} />;
}

function RoomProfileLoading({ dm }: { dm?: boolean }) {
  return (
    <Box grow="Yes" gap="300">
      <Avatar className={styleCss.AvatarPlaceholder} shape={roomAvatarShape(dm)} />
      <Box grow="Yes" direction="Column" gap="100">
        <Box gap="200" alignItems="Center">
          <Box className={styleCss.LinePlaceholder} shrink="No" style={{ maxWidth: toRem(80) }} />
        </Box>
        <Box gap="200" alignItems="Center">
          <Box className={styleCss.LinePlaceholder} shrink="No" style={{ maxWidth: toRem(40) }} />
          <Box
            className={styleCss.LinePlaceholder}
            shrink="No"
            style={{
              maxWidth: toRem(120),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

type RoomProfileErrorProps = {
  roomId: string;
  inaccessibleRoom: boolean;
  suggested?: boolean;
  via?: string[];
  dm?: boolean;
};
function RoomProfileError({ roomId, suggested, inaccessibleRoom, via, dm }: RoomProfileErrorProps) {
  return (
    <Box grow="Yes" gap="300">
      <Avatar shape={roomAvatarShape(dm)}>
        <RoomAvatar
          roomId={roomId}
          src={undefined}
          alt={roomId}
          renderFallback={() => (
            <RoomIcon
              size="300"
              joinRule={inaccessibleRoom ? JoinRule.Invite : JoinRule.Restricted}
              filled
            />
          )}
        />
      </Avatar>
      <Box grow="Yes" direction="Column" className={css.ErrorNameContainer}>
        <Box gap="200" alignItems="Center">
          <Text size="H5" truncate>
            Unknown
          </Text>
          {suggested && (
            <Box shrink="No" alignItems="Center">
              <Badge variant="Success" fill="Soft" radii="Pill" outlined>
                <Text size="L400">Suggested</Text>
              </Badge>
            </Box>
          )}
        </Box>
        <Box gap="200" alignItems="Center">
          {inaccessibleRoom ? (
            <Badge variant="Secondary" fill="Soft" radii="300" size="500">
              <Text size="L400">Inaccessible</Text>
            </Badge>
          ) : (
            <Text size="T200" truncate>
              {roomId}
            </Text>
          )}
        </Box>
      </Box>
      {!inaccessibleRoom && <RoomJoinOrKnockButton roomId={roomId} via={via} />}
    </Box>
  );
}

type RoomProfileProps = {
  roomId: string;
  roomType?: string;
  name: string;
  topic?: string;
  avatarUrl?: string;
  suggested?: boolean;
  memberCount?: number;
  joinRule?: JoinRule;
  options?: ReactNode;
  dm?: boolean;
};
function RoomProfile({
  roomId,
  roomType,
  name,
  topic,
  avatarUrl,
  suggested,
  memberCount,
  joinRule,
  options,
  dm,
}: RoomProfileProps) {
  return (
    <Box grow="Yes" gap="300">
      <Avatar shape={roomAvatarShape(dm)}>
        <RoomAvatar
          roomId={roomId}
          src={avatarUrl}
          alt={name}
          renderFallback={() =>
            dm ? (
              <Text as="span" size="H6">
                {nameInitials(name)}
              </Text>
            ) : (
              <RoomIcon size="300" joinRule={joinRule} roomType={roomType} />
            )
          }
        />
      </Avatar>
      <Box grow="Yes" direction="Column">
        <Box gap="200" alignItems="Center">
          <Text size="H5" truncate>
            {name}
          </Text>
          {suggested && (
            <Box shrink="No" alignItems="Center">
              <Badge variant="Success" fill="Soft" radii="Pill" outlined>
                <Text size="L400">Suggested</Text>
              </Badge>
            </Box>
          )}
        </Box>
        <Box gap="200" alignItems="Center">
          {memberCount && (
            <Box shrink="No" gap="200">
              <Text
                size="T200"
                priority="300"
              >{`${formatCompactNumber(memberCount)} Members`}</Text>
            </Box>
          )}
          {memberCount && topic && (
            <Line
              variant="SurfaceVariant"
              style={{ height: toRem(12) }}
              direction="Vertical"
              size="400"
            />
          )}
          {topic && (
            <UseStateProvider initial={false}>
              {(view, setView) => (
                <>
                  <Text
                    className={css.RoomProfileTopic}
                    size="T200"
                    priority="300"
                    truncate
                    onClick={() => setView(true)}
                    onKeyDown={onEnterOrSpace(() => setView(true))}
                    tabIndex={0}
                  >
                    {topic}
                  </Text>
                  <ModalOverlay open={view} requestClose={() => setView(false)}>
                    <RoomTopicViewer
                      name={name}
                      topic={topic}
                      requestClose={() => setView(false)}
                    />
                  </ModalOverlay>
                </>
              )}
            </UseStateProvider>
          )}
        </Box>
      </Box>
      {options}
    </Box>
  );
}

type RoomItemCardProps = {
  item: HierarchyItem;
  loading: boolean;
  error: Error | null;
  summary: IHierarchyRoom | undefined;
  dm?: boolean;
  firstChild?: boolean;
  lastChild?: boolean;
  onOpen: MouseEventHandler<HTMLButtonElement>;
  options?: ReactNode;
  before?: ReactNode;
  after?: ReactNode;
  onDragging: (item?: HierarchyItem) => void;
  canReorder: boolean;
  getRoom: (roomId: string) => Room | undefined;
};
export const RoomItemCard = as<'div', RoomItemCardProps>(
  (
    {
      item,
      loading,
      error,
      summary,
      dm,
      onOpen,
      options,
      before,
      after,
      onDragging,
      canReorder,
      getRoom,
      ...props
    },
    ref
  ) => {
    const mx = useMatrixClient();
    const useAuthentication = useMediaAuthentication();
    const { roomId, content } = item;
    const room = getRoom(roomId);
    const targetRef = useRef<HTMLDivElement>(null);
    const targetHandleRef = useRef<HTMLDivElement>(null);
    useDraggableItem(item, targetRef, onDragging, targetHandleRef);

    const joined = room?.getMyMembership() === KnownMembership.Join;

    return (
      <SequenceCard
        className={css.RoomItemCard}
        variant="SurfaceVariant"
        gap="300"
        alignItems="Center"
        {...props}
        ref={ref}
      >
        {before}
        <Box
          ref={canReorder ? targetRef : null}
          grow="Yes"
          className={canReorder ? dndCss.ReorderableContent : undefined}
        >
          {canReorder && <ItemDraggableTarget ref={targetHandleRef} />}
          {room ? (
            <LocalRoomSummaryLoader room={room}>
              {(localSummary) => (
                <RoomProfile
                  roomId={roomId}
                  roomType={localSummary.roomType}
                  name={localSummary.name}
                  topic={localSummary.topic}
                  dm={dm}
                  avatarUrl={
                    dm
                      ? getDirectRoomAvatarUrl(mx, room, 96, useAuthentication)
                      : getRoomAvatarUrl(mx, room, 96, useAuthentication)
                  }
                  memberCount={localSummary.memberCount}
                  suggested={content.suggested}
                  joinRule={localSummary.joinRule}
                  options={
                    joined ? (
                      <Box shrink="No" gap="100" alignItems="Center">
                        <Chip
                          data-room-id={roomId}
                          onClick={onOpen}
                          variant="Secondary"
                          fill="None"
                          size="400"
                          radii="Pill"
                          aria-label="Open Room"
                        >
                          {chipIcon(ArrowRight)}
                        </Chip>
                      </Box>
                    ) : (
                      <RoomJoinOrKnockButton
                        roomId={roomId}
                        via={content.via}
                        joinRule={localSummary.joinRule}
                      />
                    )
                  }
                />
              )}
            </LocalRoomSummaryLoader>
          ) : (
            <>
              {!summary &&
                (error ? (
                  <RoomProfileError
                    roomId={roomId}
                    inaccessibleRoom={false}
                    suggested={content.suggested}
                    via={content.via}
                    dm={dm}
                  />
                ) : (
                  <>
                    {loading && <RoomProfileLoading dm={dm} />}
                    {!loading && (
                      <RoomProfileError
                        roomId={roomId}
                        inaccessibleRoom
                        suggested={content.suggested}
                        via={content.via}
                        dm={dm}
                      />
                    )}
                  </>
                ))}
              {summary && (
                <RoomProfile
                  roomId={roomId}
                  roomType={summary.room_type}
                  name={summary.name || summary.canonical_alias || roomId}
                  topic={summary.topic}
                  dm={dm}
                  avatarUrl={
                    summary?.avatar_url
                      ? (mxcUrlToHttp(mx, summary.avatar_url, useAuthentication, 96, 96, 'crop') ??
                        undefined)
                      : undefined
                  }
                  memberCount={summary.num_joined_members}
                  suggested={content.suggested}
                  joinRule={summary.join_rule}
                  options={
                    <RoomJoinOrKnockButton
                      roomId={roomId}
                      via={content.via}
                      joinRule={summary.join_rule}
                    />
                  }
                />
              )}
            </>
          )}
        </Box>
        {options}
        {after}
      </SequenceCard>
    );
  }
);
